/**
 * CancellationModal Component (Reusable)
 *
 * Modal reutilizable para capturar razones de cancelación.
 * Flujo de 2 pasos:
 *   1. Seleccionar razón principal (obligatoria) → "Confirmar" (guarda solo principal) o "Razones adicionales" (va al paso 2)
 *   2. Seleccionar razones adicionales (checkboxes) + notas → confirmar
 *
 * La razón principal define el business_status resultante del lead.
 */

import { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { cn } from '@/shared/utils/cn';
import {
  XIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StarIcon,
  PlusIcon,
} from 'lucide-react';
import {
  CANCELLATION_CATEGORIES,
  CANCELLATION_REASONS,
  getReasonsForContext,
  getCategoriesForContext,
  type CancellationReason,
  type CancellationStatus,
} from '@/modules/leads/constants/cancellationReasons';

export interface CancellationModalResult {
  reasonCode: string;
  reasonText: string;
  cancellationCategory: string;
  resultStatus: CancellationStatus;
  notes?: string;
  /** Contiene todas las razones seleccionadas (principal como primera) */
  selectedReasons?: Array<{
    reasonCode: string;
    reasonText: string;
    cancellationCategory: string;
    resultStatus: CancellationStatus;
  }>;
}

export interface CancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CancellationModalResult) => void;
  /** Nombre del elemento que se está cancelando (ej: nombre del lead) */
  entityName?: string;
  /** Texto personalizado para el prompt */
  categoryPrompt?: string;
  /** Texto del botón de confirmación (default: "Confirmar cancelación") */
  confirmLabel?: string;
  /** Ocultar etiquetas de estado (Descartado, No interesado) */
  hideStatusLabels?: boolean;
  /** Permitir seleccionar múltiples razones (default: true) */
  multiSelect?: boolean;
  /** Contexto del usuario: 'lead' (portal público) o 'advisor' (CRM). Filtra razones por disponibilidad. */
  viewerContext?: 'lead' | 'advisor';
}

type Step = 'select' | 'additional';

export function CancellationModal({
  isOpen,
  onClose,
  onSubmit,
  entityName,
  categoryPrompt,
  confirmLabel = 'Confirmar cancelación',
  hideStatusLabels = true,
  multiSelect = true,
  viewerContext,
}: CancellationModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [primaryReason, setPrimaryReason] = useState<CancellationReason | null>(null);
  const [additionalReasons, setAdditionalReasons] = useState<CancellationReason[]>([]);
  const [notes, setNotes] = useState('');
  // Track collapsed categories (all expanded by default)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());

  const handlePrimarySelect = (reason: CancellationReason) => {
    if (primaryReason?.code === reason.code) {
      // Deselect
      setPrimaryReason(null);
    } else {
      setPrimaryReason(reason);
      // Remove from additional if it was there
      setAdditionalReasons(prev => prev.filter(r => r.code !== reason.code));
    }
  };

  const handleAdditionalToggle = (reason: CancellationReason) => {
    setAdditionalReasons(prev => {
      const exists = prev.some(r => r.code === reason.code);
      if (exists) return prev.filter(r => r.code !== reason.code);
      return [...prev, reason];
    });
  };

  const toggleCategory = (code: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleGoToAdditional = () => {
    if (!primaryReason) return;
    setStep('additional');
  };

  const handleSubmit = () => {
    if (!primaryReason) return;

    const primaryCat = CANCELLATION_CATEGORIES.find(c => c.code === primaryReason.category);

    // Build selectedReasons: primary first, then additional
    const allReasons = [primaryReason, ...additionalReasons];

    onSubmit({
      reasonCode: primaryReason.code,
      reasonText: allReasons.map(r => r.name).join(', '),
      cancellationCategory: primaryCat?.code || primaryReason.category,
      resultStatus: primaryReason.resultStatus,
      notes: notes || undefined,
      selectedReasons: allReasons.map(r => ({
        reasonCode: r.code,
        reasonText: r.name,
        cancellationCategory: r.category,
        resultStatus: r.resultStatus,
      })),
    });
    handleClose();
  };

  const handleClose = () => {
    setStep('select');
    setPrimaryReason(null);
    setAdditionalReasons([]);
    setNotes('');
    setCollapsedCategories(new Set());
    onClose();
  };

  const handleBack = () => {
    setStep('select');
  };

  const defaultPrompt = entityName
    ? `¿Por qué se cancela "${entityName}"?`
    : '¿Cuál es la razón principal de cancelación?';

  // Filter reasons and categories by viewer context
  const filteredReasons = viewerContext ? getReasonsForContext(viewerContext) : CANCELLATION_REASONS;
  const filteredCategories = viewerContext ? getCategoriesForContext(viewerContext) : CANCELLATION_CATEGORIES;

  // Group reasons by category
  const reasonsByCategory = filteredCategories.map(cat => ({
    category: cat,
    reasons: filteredReasons.filter(r => r.category === cat.code),
  }));

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[99999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={cn(
                'w-full max-w-lg transform rounded-2xl',
                'bg-white dark:bg-zinc-900 backdrop-blur-xl',
                'border border-gray-200 dark:border-zinc-700',
                'shadow-2xl transition-all',
                'flex flex-col max-h-[85vh]'
              )}>
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    {step !== 'select' && (
                      <button
                        onClick={handleBack}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <ChevronLeftIcon className="w-4 h-4 text-gray-500" />
                      </button>
                    )}
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white">
                      {step === 'select'
                        ? 'Razón de cancelación principal'
                        : 'Razones adicionales'
                      }
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={handleClose}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                  >
                    <XIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col min-h-0">
                  {/* Step 1: Select primary reason only */}
                  {step === 'select' && (
                    <>
                      {/* Fixed top section: prompt */}
                      <div className="px-6 pt-5 pb-3 flex-shrink-0 border-b border-gray-100 dark:border-zinc-800">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                          {categoryPrompt || defaultPrompt}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Selecciona la razón principal que define el estado del lead.
                        </p>
                      </div>

                      {/* Scrollable categories - primary selection only (radio) */}
                      <div className="flex-1 overflow-y-auto min-h-0 px-6 py-3">
                      <div className="space-y-3">
                        {reasonsByCategory.map(({ category, reasons }) => {
                          const Icon = category.icon;
                          const isCollapsed = collapsedCategories.has(category.code);
                          const selectedCount = reasons.filter(r => r.code === primaryReason?.code).length;

                          return (
                            <div key={category.code} className="rounded-xl border border-gray-100 dark:border-zinc-800 overflow-hidden">
                              {/* Category header */}
                              <button
                                onClick={() => toggleCategory(category.code)}
                                className={cn(
                                  'w-full flex items-center gap-3 px-4 py-2.5',
                                  'bg-gray-50/80 dark:bg-zinc-800/50',
                                  'hover:bg-gray-100 dark:hover:bg-zinc-800',
                                  'transition-colors text-left'
                                )}
                              >
                                <div
                                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${category.color}15` }}
                                >
                                  <Icon className="w-4 h-4" style={{ color: category.color }} />
                                </div>
                                <span className="flex-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                  {category.name}
                                </span>
                                {selectedCount > 0 && (
                                  <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                                    {selectedCount}
                                  </span>
                                )}
                                {isCollapsed
                                  ? <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                                  : <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                                }
                              </button>

                              {/* Reasons list - radio buttons only */}
                              {!isCollapsed && (
                                <div className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                                  {reasons.map((reason) => {
                                    const isPrimary = primaryReason?.code === reason.code;

                                    return (
                                      <button
                                        key={reason.code}
                                        onClick={() => handlePrimarySelect(reason)}
                                        className={cn(
                                          'w-full flex items-center gap-3 px-4 py-2.5',
                                          'hover:bg-gray-50 dark:hover:bg-zinc-800/30',
                                          'transition-all duration-150',
                                          'text-left',
                                          isPrimary && 'bg-red-50/50 dark:bg-red-900/15',
                                        )}
                                      >
                                        <div className={cn(
                                          'rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                                          isPrimary
                                            ? 'border-red-500'
                                            : 'border-gray-300 dark:border-zinc-600'
                                        )}
                                        style={{ width: '18px', height: '18px' }}
                                        >
                                          {isPrimary && (
                                            <div className="rounded-full bg-red-500" style={{ width: '10px', height: '10px' }} />
                                          )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm text-gray-900 dark:text-white">
                                            {reason.name}
                                          </span>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      </div>
                    </>
                  )}

                  {/* Step 2: Additional reasons + notes */}
                  {step === 'additional' && primaryReason && (
                    <>
                      {/* Fixed top: primary reason badge */}
                      <div className="px-6 pt-5 pb-3 flex-shrink-0 border-b border-gray-100 dark:border-zinc-800">
                        {/* Primary reason summary */}
                        {(() => {
                          const cat = CANCELLATION_CATEGORIES.find(c => c.code === primaryReason.category);
                          return (
                            <div className="p-2.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 mb-3">
                              <div className="flex items-center gap-2">
                                <StarIcon className="w-3.5 h-3.5 text-red-500 fill-red-500 flex-shrink-0" />
                                {cat && <cat.icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: cat.color }} />}
                                <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{primaryReason.name}</span>
                                <span className="text-[10px] font-medium text-red-500 dark:text-red-400 ml-auto flex-shrink-0 uppercase">Principal</span>
                              </div>
                            </div>
                          );
                        })()}
                        <p className="text-base font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Selecciona razones adicionales
                        </p>

                        {/* Additional reasons tags */}
                        {additionalReasons.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {additionalReasons.map(r => {
                              const rCat = CANCELLATION_CATEGORIES.find(c => c.code === r.category);
                              return (
                                <span
                                  key={r.code}
                                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800/40"
                                >
                                  {rCat && <rCat.icon className="w-3 h-3" style={{ color: rCat.color }} />}
                                  {r.name}
                                  <button
                                    onClick={() => handleAdditionalToggle(r)}
                                    className="ml-0.5 hover:text-orange-900"
                                  >
                                    <XIcon className="w-3 h-3" />
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Scrollable categories - checkboxes, skip primary */}
                      <div className="flex-1 overflow-y-auto min-h-0 px-6 py-3">
                      <div className="space-y-3">
                        {reasonsByCategory.map(({ category, reasons }) => {
                          const Icon = category.icon;
                          const isCollapsed = collapsedCategories.has(category.code);
                          const selectedCount = reasons.filter(r =>
                            additionalReasons.some(a => a.code === r.code)
                          ).length;

                          return (
                            <div key={category.code} className="rounded-xl border border-gray-100 dark:border-zinc-800 overflow-hidden">
                              {/* Category header */}
                              <button
                                onClick={() => toggleCategory(category.code)}
                                className={cn(
                                  'w-full flex items-center gap-3 px-4 py-2.5',
                                  'bg-gray-50/80 dark:bg-zinc-800/50',
                                  'hover:bg-gray-100 dark:hover:bg-zinc-800',
                                  'transition-colors text-left'
                                )}
                              >
                                <div
                                  className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${category.color}15` }}
                                >
                                  <Icon className="w-4 h-4" style={{ color: category.color }} />
                                </div>
                                <span className="flex-1 text-sm font-semibold text-gray-700 dark:text-gray-200">
                                  {category.name}
                                </span>
                                {selectedCount > 0 && (
                                  <span className="w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">
                                    {selectedCount}
                                  </span>
                                )}
                                {isCollapsed
                                  ? <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                                  : <ChevronUpIcon className="w-4 h-4 text-gray-400" />
                                }
                              </button>

                              {/* Reasons list - checkboxes, skip primary */}
                              {!isCollapsed && (
                                <div className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                                  {reasons.map((reason) => {
                                    const isPrimary = primaryReason?.code === reason.code;
                                    const isAdditional = additionalReasons.some(r => r.code === reason.code);

                                    // Show primary as disabled
                                    if (isPrimary) {
                                      return (
                                        <div
                                          key={reason.code}
                                          className="flex items-center gap-3 px-4 py-2.5 bg-red-50/30 dark:bg-red-900/10 opacity-60"
                                        >
                                          <StarIcon className="w-4 h-4 text-red-400 fill-red-400 flex-shrink-0" />
                                          <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                                            {reason.name} (principal)
                                          </span>
                                        </div>
                                      );
                                    }

                                    return (
                                      <button
                                        key={reason.code}
                                        onClick={() => handleAdditionalToggle(reason)}
                                        className={cn(
                                          'w-full flex items-center gap-3 px-4 py-2.5',
                                          'hover:bg-gray-50 dark:hover:bg-zinc-800/30',
                                          'transition-all duration-150',
                                          'text-left',
                                          isAdditional && 'bg-orange-50/50 dark:bg-orange-900/10',
                                        )}
                                      >
                                        <div className={cn(
                                          'rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                                          isAdditional
                                            ? 'bg-orange-500 border-orange-500'
                                            : 'border-gray-300 dark:border-zinc-600'
                                        )}
                                        style={{ width: '18px', height: '18px' }}
                                        >
                                          {isAdditional && (
                                            <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                                              <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <span className="text-sm text-gray-900 dark:text-white">
                                            {reason.name}
                                          </span>
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Notes field inline */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nota adicional (opcional)
                        </label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                          placeholder="Contexto adicional sobre esta cancelación..."
                          rows={3}
                          maxLength={200}
                          className={cn(
                            'w-full px-4 py-3 rounded-xl',
                            'bg-white dark:bg-zinc-800',
                            'border border-gray-200 dark:border-zinc-700',
                            'focus:ring-2 focus:ring-red-500/50 focus:border-red-500',
                            'text-gray-900 dark:text-white placeholder-gray-400',
                            'transition-all duration-200',
                            'text-sm'
                          )}
                        />
                        <p className="text-xs text-gray-400 mt-1 text-right">
                          {notes.length}/200
                        </p>
                      </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Footer - fixed at bottom */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-zinc-700 flex-shrink-0">
                  {step === 'select' && (
                    <div className="flex gap-3">
                      {multiSelect && (
                        <button
                          onClick={handleGoToAdditional}
                          disabled={!primaryReason}
                          className={cn(
                            'flex-1 py-3 px-4 rounded-xl font-semibold text-sm inline-flex items-center justify-center gap-2',
                            'transition-all duration-200',
                            primaryReason
                              ? 'bg-gray-100 dark:bg-zinc-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-zinc-600 hover:bg-gray-200 dark:hover:bg-zinc-600 shadow-sm'
                              : 'bg-gray-100 dark:bg-zinc-800 text-gray-400 border border-gray-200 dark:border-zinc-700 cursor-not-allowed'
                          )}
                        >
                          <PlusIcon className="w-4 h-4" />
                          Razones adicionales
                        </button>
                      )}
                      <button
                        onClick={handleSubmit}
                        disabled={!primaryReason}
                        className={cn(
                          'flex-1 py-3 px-4 rounded-xl font-semibold text-sm',
                          'transition-all duration-200',
                          primaryReason
                            ? 'bg-zinc-800 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-700 dark:hover:bg-zinc-200 shadow-sm'
                            : 'bg-gray-100 dark:bg-zinc-800 text-gray-400 cursor-not-allowed'
                        )}
                      >
                        Confirmar
                      </button>
                    </div>
                  )}

                  {step === 'additional' && (
                    <button
                      onClick={handleSubmit}
                      disabled={additionalReasons.length === 0}
                      className={cn(
                        'w-full py-3 px-4 rounded-xl font-semibold text-sm',
                        'transition-all duration-200',
                        additionalReasons.length > 0
                          ? 'bg-zinc-800 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-700 dark:hover:bg-zinc-200 shadow-sm'
                          : 'bg-gray-100 dark:bg-zinc-800 text-gray-400 cursor-not-allowed'
                      )}
                    >
                      {additionalReasons.length > 0
                        ? `${confirmLabel} (${additionalReasons.length + 1} razones)`
                        : 'Selecciona al menos una razón adicional'
                      }
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
